import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { api, call } from "./client";
import type {
  CmdbSourceIn,
  ConnectorSave,
  ConnectorTestIn,
  DraftStatus,
  FlowDetail,
  FlowGraphIn,
  KnowledgeConnector,
  KnowledgeSourceIn,
  NotifyEscalationIn,
  PolicyScope,
  RetentionIn,
  StatusBarData,
  TestSignal,
} from "./types";

export const keys = {
  me: ["me"] as const,
  status: ["status"] as const,
  overview: ["overview"] as const,
  needsYou: ["needs-you"] as const,
  approval: (id: string) => ["approval", id] as const,
  cases: ["cases"] as const,
  case: (n: number) => ["case", n] as const,
  warRoom: (id: string) => ["war-room", id] as const,
  flows: ["flows"] as const,
  flow: (key: string) => ["flow", key] as const,
  autonomy: ["autonomy"] as const,
  crew: ["crew"] as const,
  agent: (id: string) => ["agent", id] as const,
  guardian: ["guardian"] as const,
  outcomes: ["outcomes"] as const,
  connections: ["connections"] as const,
  users: ["users"] as const,
  audit: ["audit"] as const,
  conversations: ["conversations"] as const,
  signals: ["signals"] as const,
  conversation: (id: string) => ["conversation", id] as const,
  connectors: ["connectors"] as const,
  connector: (key: string) => ["connector", key] as const,
  palette: ["flow-palette"] as const,
  templates: ["flow-templates"] as const,
  template: (key: string) => ["flow-template", key] as const,
  testRuns: (key: string) => ["flow-test-runs", key] as const,
  flowRuns: (key: string) => ["flow-runs", key] as const,
  notifications: ["notifications"] as const,
  retention: ["retention"] as const,
  knowledge: ["knowledge"] as const,
  knowledgeSearch: (q: string, source: string | null) => ["knowledge-search", q, source] as const,
  knowledgeDoc: (id: string) => ["knowledge-doc", id] as const,
  knowledgeDrafts: (status: string) => ["knowledge-drafts", status] as const,
  caseKnowledge: (n: number) => ["case-knowledge", n] as const,
  estate: ["estate"] as const,
  estateWatches: ["estate", "watches"] as const,
  estateItem: (name: string) => ["estate", "item", name] as const,
  estateSeverity: ["estate", "severity"] as const,
};

/* Reads ---------------------------------------------------------------------------------- */

export const useMe = () =>
  useQuery({ queryKey: keys.me, queryFn: () => call(api.GET("/api/v1/me")) });

export const useStatusBar = () =>
  useQuery({
    queryKey: keys.status,
    queryFn: () => call(api.GET("/api/v1/status")),
    refetchInterval: 60_000,
  });

export type OverviewPeriod = "today" | "7d" | "30d";

export const useOverview = (period: OverviewPeriod = "today") =>
  useQuery({
    queryKey: [...keys.overview, period],
    queryFn: () => call(api.GET("/api/v1/overview", { params: { query: { period } } })),
    placeholderData: (previous) => previous,
  });

export const useNeedsYou = () =>
  useQuery({ queryKey: keys.needsYou, queryFn: () => call(api.GET("/api/v1/needs-you")) });

export const useApproval = (id: string | undefined) =>
  useQuery({
    queryKey: keys.approval(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      call(
        api.GET("/api/v1/approvals/{approval_id}", { params: { path: { approval_id: id ?? "" } } }),
      ),
  });

export const useCases = () =>
  useQuery({ queryKey: keys.cases, queryFn: () => call(api.GET("/api/v1/cases")) });

export const useCase = (n: number) =>
  useQuery({
    queryKey: keys.case(n),
    enabled: Number.isFinite(n),
    queryFn: () => call(api.GET("/api/v1/cases/{number}", { params: { path: { number: n } } })),
  });

export const useCaseTimeline = (n: number) =>
  useQuery({
    queryKey: [...keys.case(n), "timeline"],
    enabled: Number.isFinite(n),
    queryFn: () =>
      call(api.GET("/api/v1/cases/{number}/timeline", { params: { path: { number: n } } })),
  });

export const useWarRoom = (id?: string, enabled = true) =>
  useQuery({
    queryKey: keys.warRoom(id ?? "current"),
    enabled,
    queryFn: () =>
      id
        ? call(api.GET("/api/v1/war-rooms/{room_id}", { params: { path: { room_id: id } } }))
        : call(api.GET("/api/v1/war-rooms/current")),
  });

export const useFlows = () =>
  useQuery({ queryKey: keys.flows, queryFn: () => call(api.GET("/api/v1/flows")) });

export const useFlow = (key: string | undefined) =>
  useQuery({
    queryKey: keys.flow(key ?? ""),
    enabled: Boolean(key),
    queryFn: () => call(api.GET("/api/v1/flows/{key}", { params: { path: { key: key ?? "" } } })),
  });

export const useAutonomy = () =>
  useQuery({ queryKey: keys.autonomy, queryFn: () => call(api.GET("/api/v1/autonomy")) });

export const useCrew = () =>
  useQuery({ queryKey: keys.crew, queryFn: () => call(api.GET("/api/v1/crew")) });

export const useAgent = (id: string | undefined) =>
  useQuery({
    queryKey: keys.agent(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      call(api.GET("/api/v1/agents/{agent_id}", { params: { path: { agent_id: id ?? "" } } })),
  });

export const useGuardian = () =>
  useQuery({ queryKey: keys.guardian, queryFn: () => call(api.GET("/api/v1/guardian")) });

export const useOutcomes = () =>
  useQuery({ queryKey: keys.outcomes, queryFn: () => call(api.GET("/api/v1/outcomes")) });

export const useConnections = () =>
  useQuery({ queryKey: keys.connections, queryFn: () => call(api.GET("/api/v1/connections")) });

export const useUsers = () =>
  useQuery({ queryKey: keys.users, queryFn: () => call(api.GET("/api/v1/users")) });

export const useAudit = (limit = 20) =>
  useQuery({
    queryKey: [...keys.audit, limit],
    queryFn: () => call(api.GET("/api/v1/audit", { params: { query: { limit } } })),
  });

export const useConversations = () =>
  useQuery({
    queryKey: keys.conversations,
    queryFn: () => call(api.GET("/api/v1/ask/conversations")),
  });

export const useConversation = (id: string | undefined) =>
  useQuery({
    queryKey: keys.conversation(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      call(
        api.GET("/api/v1/ask/conversations/{conversation_id}", {
          params: { path: { conversation_id: id ?? "" } },
        }),
      ),
  });

export const useSignals = (limit = 30) =>
  useQuery({
    queryKey: [...keys.signals, limit],
    queryFn: () => call(api.GET("/api/v1/signals", { params: { query: { limit } } })),
    refetchInterval: 30_000,
  });

export const useConnectors = () =>
  useQuery({ queryKey: keys.connectors, queryFn: () => call(api.GET("/api/v1/connectors")) });

export const useConnector = (key: string | null | undefined) =>
  useQuery({
    queryKey: keys.connector(key ?? ""),
    enabled: Boolean(key),
    queryFn: () =>
      call(api.GET("/api/v1/connectors/{key}", { params: { path: { key: key ?? "" } } })),
  });

/** Every action, trigger and crew task a flow can use, with each connector's state. */
export const usePalette = () =>
  useQuery({
    queryKey: keys.palette,
    queryFn: () => call(api.GET("/api/v1/flow-palette")),
    staleTime: 60_000,
  });

export const useTemplates = () =>
  useQuery({
    queryKey: keys.templates,
    queryFn: () => call(api.GET("/api/v1/flow-templates")),
    staleTime: 5 * 60_000,
  });

export const useTemplate = (key: string | undefined) =>
  useQuery({
    queryKey: keys.template(key ?? ""),
    enabled: Boolean(key),
    queryFn: () =>
      call(api.GET("/api/v1/flow-templates/{key}", { params: { path: { key: key ?? "" } } })),
  });

export const useTestRuns = (key: string | undefined) =>
  useQuery({
    queryKey: keys.testRuns(key ?? ""),
    enabled: Boolean(key),
    queryFn: () =>
      call(api.GET("/api/v1/flows/{key}/test-runs", { params: { path: { key: key ?? "" } } })),
  });

export const useFlowRuns = (key: string | undefined) =>
  useQuery({
    queryKey: keys.flowRuns(key ?? ""),
    enabled: Boolean(key),
    queryFn: () =>
      call(api.GET("/api/v1/flows/{key}/runs", { params: { path: { key: key ?? "" } } })),
  });

export const useRetention = () =>
  useQuery({
    queryKey: keys.retention,
    queryFn: () => call(api.GET("/api/v1/retention")),
  });

export const useNotifySettings = () =>
  useQuery({
    queryKey: keys.notifications,
    queryFn: () => call(api.GET("/api/v1/notifications")),
  });

export const useKnowledge = () =>
  useQuery({
    queryKey: keys.knowledge,
    queryFn: () => call(api.GET("/api/v1/knowledge")),
    // While a source is being read, follow it until the sync finishes.
    refetchInterval: (query) => (query.state.data?.sources.some((s) => s.syncing) ? 3_000 : false),
  });

export const useKnowledgeSearch = (q: string, source: KnowledgeConnector | null) =>
  useQuery({
    queryKey: keys.knowledgeSearch(q, source),
    enabled: q.trim().length >= 2,
    queryFn: () =>
      call(
        api.GET("/api/v1/knowledge/search", {
          params: { query: { q: q.trim(), limit: 8, ...(source ? { source } : {}) } },
        }),
      ),
    placeholderData: (previous) => previous,
  });

export const useKnowledgeDoc = (id: string | null) =>
  useQuery({
    queryKey: keys.knowledgeDoc(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      call(api.GET("/api/v1/knowledge/docs/{doc_id}", { params: { path: { doc_id: id ?? "" } } })),
  });

export const useKnowledgeDrafts = (status: DraftStatus = "draft") =>
  useQuery({
    queryKey: keys.knowledgeDrafts(status),
    queryFn: () => call(api.GET("/api/v1/knowledge/drafts", { params: { query: { status } } })),
  });

export const useCaseKnowledge = (number: number) =>
  useQuery({
    queryKey: keys.caseKnowledge(number),
    queryFn: () =>
      call(api.GET("/api/v1/cases/{number}/knowledge", { params: { path: { number } } })),
  });

export const useEstate = () =>
  useQuery({
    queryKey: keys.estate,
    queryFn: () => call(api.GET("/api/v1/estate")),
    // While the CMDB is being read, follow it until the sync finishes.
    refetchInterval: (query) => (query.state.data?.cmdb.syncing ? 3_000 : false),
  });

export const useEstateWatches = () =>
  useQuery({
    queryKey: keys.estateWatches,
    queryFn: () => call(api.GET("/api/v1/estate/watches")),
    // Readings arrive once a minute; live events refresh them, this is the fallback.
    refetchInterval: 60_000,
  });

export const useEstateItem = (name: string | null) =>
  useQuery({
    queryKey: keys.estateItem(name ?? ""),
    enabled: Boolean(name),
    queryFn: () =>
      call(api.GET("/api/v1/estate/items/{name}", { params: { path: { name: name ?? "" } } })),
  });

export const useEstateSeverity = () =>
  useQuery({
    queryKey: keys.estateSeverity,
    queryFn: () => call(api.GET("/api/v1/estate/severity")),
  });

/* Writes --------------------------------------------------------------------------------- */

function useInvalidate() {
  const qc = useQueryClient();
  return (...groups: QueryKey[]) =>
    Promise.all(groups.map((queryKey) => qc.invalidateQueries({ queryKey })));
}

export function useDecision(id: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      verb,
      note,
    }: {
      verb: "approve" | "decline" | "acknowledge";
      note?: string;
    }) => {
      const params = { params: { path: { approval_id: id } }, body: { note: note ?? null } };
      if (verb === "approve")
        return call(api.POST("/api/v1/approvals/{approval_id}/approve", params));
      if (verb === "decline")
        return call(api.POST("/api/v1/approvals/{approval_id}/decline", params));
      return call(api.POST("/api/v1/approvals/{approval_id}/acknowledge", params));
    },
    onSuccess: () =>
      invalidate(
        keys.needsYou,
        keys.approval(id),
        keys.overview,
        keys.me,
        keys.cases,
        keys.autonomy,
        // A flow's review moves it to Shadow or back to Draft.
        keys.flows,
        ["flow"],
      ),
  });
}

/** Ask Investigator to read the connected tools about a case again, now. */
export function useInvestigate(number: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      call(api.POST("/api/v1/cases/{number}/investigate", { params: { path: { number } } })),
    onSuccess: (data) => {
      qc.setQueryData(keys.case(number), data);
      void qc.invalidateQueries({ queryKey: keys.cases });
    },
  });
}

export function useCloseCase(number: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (note: string) =>
      call(
        api.POST("/api/v1/cases/{number}/close", { params: { path: { number } }, body: { note } }),
      ),
    onSuccess: () =>
      invalidate(keys.case(number), keys.cases, keys.needsYou, keys.overview, keys.me),
  });
}

export function useSendTestSignal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: TestSignal) => call(api.POST("/api/v1/signals/test", { body })),
    onSuccess: () => invalidate(keys.signals, keys.connections),
  });
}

export function useSetFlowEnabled() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      call(api.PATCH("/api/v1/flows/{key}", { params: { path: { key } }, body: { enabled } })),
    onSuccess: (_d, v) => invalidate(keys.flows, keys.flow(v.key), keys.autonomy),
  });
}

/** Draft a flow from plain English, or (with refine_key) change a draft as described. */
export function useDraftFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: string | { description: string; refine_key?: string | null }) => {
      const body = typeof input === "string" ? { description: input } : input;
      return call(
        api.POST("/api/v1/flows/drafts", {
          body: { description: body.description, refine_key: body.refine_key ?? null },
        }),
      );
    },
    onSuccess: (flow) => invalidate(keys.flows, keys.autonomy, keys.flow(flow.key)),
  });
}

/** Copy a template into a new draft. */
export function useUseTemplate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) =>
      call(api.POST("/api/v1/flow-templates/{key}/use", { params: { path: { key } } })),
    onSuccess: () => invalidate(keys.flows, keys.autonomy),
  });
}

/** Save an edited graph. Refused (409) when someone saved a newer version first. */
export function useSaveGraph(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: FlowGraphIn) =>
      call(api.PUT("/api/v1/flows/{key}/graph", { params: { path: { key } }, body })),
    onSuccess: (flow: FlowDetail) => {
      qc.setQueryData(keys.flow(key), flow);
      return invalidate(keys.flows);
    },
  });
}

/** Where one kind of news goes. */
export function useNotifyRoute() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      kind,
      ...body
    }: {
      kind: string;
      teams: boolean;
      slack: boolean;
      email: boolean;
      slack_channel: string | null;
    }) =>
      call(api.PUT("/api/v1/notifications/routes/{kind}", { params: { path: { kind } }, body })),
    onSuccess: (data) => qc.setQueryData(keys.notifications, data),
  });
}

/** Send a test message to one channel now. */
/** Choose when a fix nobody decides is sent again, pages on-call, and goes back to a person. */
export function useNotifyEscalation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: NotifyEscalationIn) =>
      call(api.PUT("/api/v1/notifications/escalation", { body })),
    onSuccess: (data) => qc.setQueryData(keys.notifications, data),
  });
}

/** Choose how long each kind of data is kept, or keep it for good. */
export function useSaveRetention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RetentionIn) => call(api.PUT("/api/v1/retention", { body })),
    onSuccess: (data) => qc.setQueryData(keys.retention, data),
  });
}

export function useNotifyTest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channel: "teams" | "slack" | "email") =>
      call(api.POST("/api/v1/notifications/test", { body: { channel } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.notifications }),
  });
}

/** Post a war room status update to Teams and Slack. */
export function usePostWarRoomUpdate(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      call(
        api.POST("/api/v1/war-rooms/{room_id}/updates", {
          params: { path: { room_id: roomId } },
          body: { text },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(keys.warRoom(roomId), data);
      // The page may show it as the current room, under its own key.
      void qc.invalidateQueries({ queryKey: ["war-room"] });
      void qc.invalidateQueries({ queryKey: keys.notifications });
    },
  });
}

/** Close a war room before its case closes, with an optional note for its timeline. */
export function useCloseWarRoom(roomId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note: string) =>
      call(
        api.POST("/api/v1/war-rooms/{room_id}/close", {
          params: { path: { room_id: roomId } },
          body: { note: note.trim() || null },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(keys.warRoom(roomId), data);
      // The page may show it as the current room, the sidebar links to it, and its case says
      // whether it's open.
      for (const queryKey of [["war-room"], ["war-rooms"], keys.cases, ["case"], keys.audit]) {
        void qc.invalidateQueries({ queryKey });
      }
    },
  });
}

/** Start a flow that runs by hand or on a schedule, now. */
export function useStartFlowRun(key: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      call(
        api.POST("/api/v1/flows/{key}/runs", {
          params: { path: { key } },
          body: { reason: reason.trim() || null },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(keys.flowRuns(key), data);
      void qc.invalidateQueries({ queryKey: keys.flow(key) });
    },
  });
}

/** Send a draft for review: approvers and admins accept it into Shadow from Needs you. */
export function useSendForReview(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => call(api.POST("/api/v1/flows/{key}/review", { params: { path: { key } } })),
    onSuccess: (flow) => {
      qc.setQueryData(keys.flow(key), flow);
      return invalidate(keys.flows, keys.needsYou, keys.overview, keys.me);
    },
  });
}

/** Take a draft back from review before anyone decides it (whoever sent it). */
export function useWithdrawReview(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () =>
      call(api.POST("/api/v1/flows/{key}/review/withdraw", { params: { path: { key } } })),
    onSuccess: (flow) => {
      qc.setQueryData(keys.flow(key), flow);
      return invalidate(keys.flows, keys.needsYou, keys.overview, keys.me);
    },
  });
}

/** Take a flow in Shadow live; Guardian replays it on sample data first. */
export function useGoLive(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => call(api.POST("/api/v1/flows/{key}/go-live", { params: { path: { key } } })),
    onSuccess: (flow) => qc.setQueryData(keys.flow(key), flow),
    // Passed or blocked, the replay is kept: one of the flow's test runs, and Guardian's record.
    onSettled: () =>
      invalidate(
        keys.flow(key),
        keys.flows,
        keys.autonomy,
        keys.guardian,
        keys.testRuns(key),
        keys.flowRuns(key),
      ),
  });
}

export function useCopyFlow() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) =>
      call(api.POST("/api/v1/flows/{key}/copy", { params: { path: { key } } })),
    onSuccess: () => invalidate(keys.flows, keys.autonomy),
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (key: string) => {
      const { response, error } = await api.DELETE("/api/v1/flows/{key}", {
        params: { path: { key } },
      });
      if (!response.ok) {
        const body = (error ?? {}) as { detail?: string; title?: string };
        throw new Error(body.detail ?? body.title ?? "Couldn't remove the draft.");
      }
      return key;
    },
    onSuccess: (key) => {
      qc.removeQueries({ queryKey: keys.flow(key) });
      return invalidate(keys.flows, keys.autonomy);
    },
  });
}

/** Save a connector's credentials and settings (admins). */
export function useSaveConnector(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: ConnectorSave) =>
      call(api.PUT("/api/v1/connectors/{key}/credentials", { params: { path: { key } }, body })),
    onSuccess: (detail) => {
      qc.setQueryData(keys.connector(key), detail);
      return invalidate(keys.connectors, keys.palette, keys.connections);
    },
  });
}

export function useRemoveConnector(key: string) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () =>
      call(api.DELETE("/api/v1/connectors/{key}/credentials", { params: { path: { key } } })),
    onSuccess: (detail) => {
      qc.setQueryData(keys.connector(key), detail);
      return invalidate(keys.connectors, keys.palette, keys.connections);
    },
  });
}

/** Test a connection: the saved values, or the form's values before saving them. */
export function useTestConnector(key: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body?: ConnectorTestIn) =>
      call(
        api.POST("/api/v1/connectors/{key}/test", {
          params: { path: { key } },
          body: body ?? null,
        }),
      ),
    onSuccess: () => invalidate(keys.connector(key), keys.connectors, keys.palette),
  });
}

/** Run one connector action now, with these inputs ("Try it"). */
export function useRunConnectorAction(key: string) {
  return useMutation({
    mutationFn: ({ action, inputs }: { action: string; inputs: Record<string, unknown> }) =>
      call(
        api.POST("/api/v1/connectors/{key}/actions/{action}/run", {
          params: { path: { key, action } },
          body: { inputs },
        }),
      ),
  });
}

export function usePromote() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: { flow_key: string; scope: PolicyScope }) =>
      call(api.POST("/api/v1/autonomy/promote", { body })),
    onSuccess: () => invalidate(keys.autonomy, keys.flows),
  });
}

export function useRuleChange() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({
      key,
      enabled,
      value,
    }: {
      key: string;
      enabled?: boolean;
      value?: Record<string, unknown>;
    }) =>
      call(
        api.PATCH("/api/v1/autonomy/rules/{key}", {
          params: { path: { key } },
          body: { enabled: enabled ?? null, value: value ?? null },
        }),
      ),
    onSuccess: () => invalidate(keys.autonomy),
  });
}

/** Hold all automatic fixes, or lift the hold. The banner and Autonomy follow at once. */
export function useHoldChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ on, note }: { on: boolean; note?: string | null }) =>
      call(api.PUT("/api/v1/hold", { body: { on, note: note ?? null } })),
    onSuccess: (hold) => {
      qc.setQueryData<StatusBarData>(keys.status, (old) => (old ? { ...old, hold } : old));
      return Promise.all(
        [keys.status, keys.autonomy, keys.audit].map((queryKey) =>
          qc.invalidateQueries({ queryKey }),
        ),
      );
    },
  });
}

export function useConfirmRule() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (key: string) =>
      call(api.POST("/api/v1/autonomy/rules/{key}/confirm", { params: { path: { key } } })),
    onSuccess: () => invalidate(keys.autonomy),
  });
}

export function useMicroAgentToggle(agentId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      call(
        api.PATCH("/api/v1/agents/{agent_id}/micro-agents/{key}", {
          params: { path: { agent_id: agentId, key } },
          body: { enabled },
        }),
      ),
    onSuccess: () => invalidate(keys.agent(agentId)),
  });
}

export function useAgentInstructions(agentId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (instructions: string) =>
      call(
        api.PATCH("/api/v1/agents/{agent_id}", {
          params: { path: { agent_id: agentId } },
          body: { instructions },
        }),
      ),
    onSuccess: () => invalidate(keys.agent(agentId)),
  });
}

export function useCorrection(agentId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (text: string) =>
      call(
        api.POST("/api/v1/agents/{agent_id}/corrections", {
          params: { path: { agent_id: agentId } },
          body: { text },
        }),
      ),
    onSuccess: () => invalidate(keys.agent(agentId)),
  });
}

export function useApplyProposal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      call(
        api.POST("/api/v1/guardian/proposals/{proposal_id}/apply", {
          params: { path: { proposal_id: id } },
        }),
      ),
    onSuccess: () => invalidate(keys.guardian),
  });
}

export function useAsk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { question: string; conversation_id?: string | null; spoken?: boolean }) =>
      call(
        api.POST("/api/v1/ask", {
          body: {
            question: body.question,
            conversation_id: body.conversation_id ?? null,
            spoken: body.spoken ?? false,
          },
        }),
      ),
    onSuccess: (data) => {
      qc.setQueryData(keys.conversation(data.id), data);
      return qc.invalidateQueries({ queryKey: keys.conversations });
    },
  });
}

export function useVerifyAudit() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => call(api.POST("/api/v1/audit/verify")),
    onSuccess: () => invalidate(keys.audit, keys.status),
  });
}

const KNOWLEDGE_KEYS: QueryKey[] = [
  ["knowledge"],
  ["knowledge-search"],
  ["knowledge-drafts"],
  ["knowledge-doc"],
  ["case-knowledge"],
  ["audit"],
];

export function useSaveKnowledgeSource() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ connector, body }: { connector: KnowledgeConnector; body: KnowledgeSourceIn }) =>
      call(
        api.PUT("/api/v1/knowledge/sources/{connector}", {
          params: { path: { connector } },
          body,
        }),
      ),
    onSuccess: () => invalidate(...KNOWLEDGE_KEYS),
  });
}

export function useSyncKnowledgeSource() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (connector: KnowledgeConnector) =>
      call(
        api.POST("/api/v1/knowledge/sources/{connector}/sync", {
          params: { path: { connector } },
        }),
      ),
    onSuccess: () => invalidate(keys.knowledge, keys.audit),
  });
}

export function useSaveDraft() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, title, body }: { id: string; title: string; body: string }) =>
      call(
        api.PATCH("/api/v1/knowledge/drafts/{draft_id}", {
          params: { path: { draft_id: id } },
          body: { title, body },
        }),
      ),
    onSuccess: () => invalidate(["knowledge-drafts"]),
  });
}

export function usePublishDraft() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      call(
        api.POST("/api/v1/knowledge/drafts/{draft_id}/publish", {
          params: { path: { draft_id: id } },
        }),
      ),
    onSettled: () => invalidate(...KNOWLEDGE_KEYS),
  });
}

export function useDiscardDraft() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (id: string) =>
      call(
        api.POST("/api/v1/knowledge/drafts/{draft_id}/discard", {
          params: { path: { draft_id: id } },
        }),
      ),
    onSuccess: () => invalidate(...KNOWLEDGE_KEYS),
  });
}

export function useDraftFromCase(number: number) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () =>
      call(api.POST("/api/v1/cases/{number}/knowledge-draft", { params: { path: { number } } })),
    onSuccess: () => invalidate(["knowledge-drafts"], keys.knowledge, keys.audit),
  });
}

export function useSaveCmdb() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: CmdbSourceIn) => call(api.PUT("/api/v1/estate/cmdb", { body })),
    onSuccess: () => invalidate(keys.estate, keys.audit),
  });
}

export function useSyncCmdb() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => call(api.POST("/api/v1/estate/cmdb/sync")),
    onSuccess: () => invalidate(keys.estate, keys.audit),
  });
}

export function useSetWatch() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ ciId, metric, enabled }: { ciId: string; metric: string; enabled: boolean }) =>
      call(
        api.PATCH("/api/v1/estate/watches/{ci_id}/{metric}", {
          params: { path: { ci_id: ciId, metric } },
          body: { enabled },
        }),
      ),
    onSuccess: () => invalidate(keys.estate, keys.audit),
  });
}

export function useTrainSeverity() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => call(api.POST("/api/v1/estate/severity/train")),
    onSuccess: () => invalidate(keys.estate, keys.audit),
  });
}
